import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mascot } from '@/components/Mascot';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { useDofiStart } from '@/hooks/useDofiStart';
import { START_TASKS, parseChoice, formatChoice, type MarkStatus } from '@/lib/dofiStart';

/**
 * «Старт с Дофи» — карточка трёх дней (30.10.2026).
 * Стоит на главной и в чате. Одно задание в день; следующее открывается завтра —
 * это и есть повод вернуться. «Частично» засчитывается: «всё или ничего»
 * для эмоционального типа — прямая дорога к заеданию.
 */
export function DofiStartCard({ place, invite = false }: { place: 'home' | 'chat'; invite?: boolean }) {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { profile } = useUserStore();
  const { startAt, day, marks, start, mark } = useDofiStart();
  const ep = profile?.eating_profile ?? null;
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem('dofi_start_later') === '1'; } catch { return false; }
  });
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [todayMeals, setTodayMeals] = useState<{ consumed_at: string; total_calories: number | null }[] | null>(null);

  const style = ep?.style ?? 'balanced';
  const task = day >= 1 && day <= 3 ? START_TASKS[style][day - 1] : null;
  const todayMark = marks.find((m) => m.day === day);

  // Задания «по дневнику» засчитываются сами, когда в дневнике есть подтверждение
  useEffect(() => {
    if (!profile?.id || !task || (task.kind !== 'lunch' && task.kind !== 'log')) return;
    const from = new Date(); from.setHours(0, 0, 0, 0);
    supabase.from('meals').select('consumed_at,total_calories').eq('user_id', profile.id)
      .gte('consumed_at', from.toISOString())
      .then(({ data }) => setTodayMeals((data as any[]) ?? []));
  }, [profile?.id, task?.kind, day]);
  const autoDone = !!todayMeals && !!task && (
    task.kind === 'lunch'
      ? todayMeals.some((m) => new Date(m.consumed_at).getHours() < 15 && (Number(m.total_calories) || 0) >= 300)
      : task.kind === 'log' ? todayMeals.length >= 3 : false);
  useEffect(() => {
    if (autoDone && (day === 1 || day === 2 || day === 3) && todayMark?.status !== 'done') mark(day, { status: 'done' });
  }, [autoDone, day]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!profile || !ep) return null;

  // Ещё не начинал — приглашение (на главной; в чате приглашает сам портрет)
  if (!startAt) {
    if (hidden || (place === 'chat' && !invite)) return null;
    return (
      <div className="card p-4 mb-4 border border-emerald-glow/30">
        <div className="flex gap-3 items-start">
          <Mascot pose="welcome" compact float={false} className="w-12 h-12 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-sm mb-1">3 дня с Дофи</div>
            <p className="text-xs text-ink-muted leading-relaxed">
              По одному короткому заданию в день — под твой тип пищевого поведения. Минута в день, без запретов и подсчётов.
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={async () => { haptic('success'); await start(); }}
            className="flex-1 py-2.5 rounded-xl bg-emerald-glow text-bg text-sm font-bold active:scale-[0.98] transition-all"
          >
            Начать сегодня
          </button>
          <button
            onClick={() => { setHidden(true); try { sessionStorage.setItem('dofi_start_later', '1'); } catch {} }}
            className="px-3 text-xs text-ink-dim"
          >
            Позже
          </button>
        </div>
      </div>
    );
  }

  // Завершён — ведём на разбор (неделю после финиша)
  if (day >= 4) {
    if (day > 10) return null;
    return (
      <button
        onClick={() => { haptic('light'); nav('/start-review'); }}
        className="w-full card p-4 mb-4 flex gap-3 items-center text-left border border-emerald-glow/40 active:scale-[0.99] transition-transform"
      >
        <Mascot pose="celebrate" compact float={false} className="w-12 h-12 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm">Три дня позади</div>
          <div className="text-xs text-emerald-glow mt-0.5">Я собрал, что изменилось — посмотри разбор</div>
        </div>
        <span className="text-emerald-glow text-lg">→</span>
      </button>
    );
  }
  if (!task) return null;

  const counts = parseChoice(todayMark?.choice);
  function tapChoice(key: string) {
    haptic('light');
    const next = { ...counts, [key]: (counts[key] || 0) + 1 };
    mark(day as 1 | 2 | 3, { status: 'done', choice: formatChoice(next) });
  }
  function setStatus(s: MarkStatus) {
    haptic(s === 'done' ? 'success' : 'light');
    mark(day as 1 | 2 | 3, { status: s });
    setNoteOpen(s === 'missed');
  }
  const prevMissing = ([1, 2] as const).filter((d) => d < day && !marks.find((m) => m.day === d));

  return (
    <div className="card p-4 mb-4 border border-emerald-glow/30">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-emerald-glow">Старт с Дофи · день {day} из 3</div>
        <div className="flex gap-1.5" aria-label={`Пройдено ${marks.length} из 3`}>
          {[1, 2, 3].map((d) => {
            const m = marks.find((x) => x.day === d);
            return (
              <span key={d} className={`w-2.5 h-2.5 rounded-full ${
                m?.status === 'done' ? 'bg-emerald-glow' : m?.status === 'partial' ? 'bg-emerald-glow/50' : m ? 'bg-ink-dim/50' : d === day ? 'border border-emerald-glow' : 'border border-line'
              }`} />
            );
          })}
        </div>
      </div>
      <div className="font-display font-semibold mb-1">{task.title}</div>
      <p className="text-sm text-ink-muted leading-relaxed mb-3">{task.text}</p>

      {task.kind === 'choice' && task.choices && (
        <div className="flex gap-2">
          {task.choices.map((c) => (
            <button key={c.key} onClick={() => tapChoice(c.key)}
              className="flex-1 py-2.5 rounded-xl border border-line bg-bg-elevated text-sm active:scale-[0.97] transition-all">
              {c.label}{counts[c.key] ? <span className="ml-1.5 text-emerald-glow font-bold">{counts[c.key]}</span> : null}
            </button>
          ))}
        </div>
      )}

      {(task.kind === 'mark' || task.kind === 'lunch' || task.kind === 'log') && (
        autoDone ? (
          <div className="text-sm text-emerald-glow">✓ Засчитано по дневнику</div>
        ) : (
          <div className="flex gap-2">
            {([['done', 'Сделал'], ['partial', 'Частично'], ['missed', 'Не вышло']] as [MarkStatus, string][]).map(([s, l]) => (
              <button key={s} onClick={() => setStatus(s)}
                className={`flex-1 py-2.5 rounded-xl border text-sm active:scale-[0.97] transition-all ${
                  todayMark?.status === s ? 'border-emerald-glow/60 bg-emerald-glow/10 text-ink' : 'border-line bg-bg-elevated text-ink-muted'
                }`}>
                {l}
              </button>
            ))}
          </div>
        )
      )}

      {noteOpen && (
        <div className="mt-3">
          <div className="text-xs text-ink-muted mb-1.5">Что помешало? Это не оценка — просто увидим, где сложнее.</div>
          <div className="flex gap-2">
            <input className="input flex-1 text-sm py-2" value={note} maxLength={300}
              onChange={(e) => setNote(e.target.value)} placeholder="Например: устал, гости, не успел" />
            <button onClick={() => { mark(day as 1 | 2 | 3, { note: note.trim() || null }); setNoteOpen(false); haptic('light'); }}
              className="px-3 rounded-xl bg-emerald-glow text-bg text-sm font-bold">ОК</button>
          </div>
        </div>
      )}

      {task.action && (
        <button onClick={() => nav(task.action!.to)} className="mt-3 text-xs text-emerald-glow underline underline-offset-4">
          {task.action.label}
        </button>
      )}

      {prevMissing.length > 0 && (
        <div className="mt-3 text-[11px] text-ink-dim">
          {prevMissing.map((d) => (
            <span key={d} className="mr-3">
              День {d} не отмечен:{' '}
              <button className="text-emerald-glow underline" onClick={() => mark(d, { status: 'done' })}>сделал</button>{' / '}
              <button className="text-ink-muted underline" onClick={() => mark(d, { status: 'partial' })}>частично</button>{' / '}
              <button className="text-ink-muted underline" onClick={() => mark(d, { status: 'missed' })}>не вышло</button>
            </span>
          ))}
        </div>
      )}

      {day < 3 && (
        <div className="mt-3 pt-3 border-t border-line text-[11px] text-ink-dim">
          Завтра откроется: {START_TASKS[style][day].title}
        </div>
      )}
      {day === 3 && (
        <div className="mt-3 pt-3 border-t border-line text-[11px] text-ink-dim">
          Завтра — разбор трёх дней: что изменилось и что ты, скорее всего, сам не заметил.
        </div>
      )}
    </div>
  );
}
